import type { ReactNode } from 'react';
import { Breadcrumbs } from '../primitives/Breadcrumbs.js';
import { Title } from '../primitives/Title.js';
import { useMediaQuery } from '../utils/useMediaQuery.js';
import styles from './PageHeader.module.css';

// Page title should guide, not dominate.
const PAGE_TITLE_FZ = 'var(--wp-type-display)';
const PAGE_TITLE_COLOR = 'color-mix(in srgb, var(--wp-text-primary) 84%, var(--wp-text-ui))';

export interface Crumb {
  label: string;
  href?: string;
}

export interface PageHeaderProps {
  title: string;
  breadcrumbs?: Crumb[];
  end?: ReactNode;
  /** Glyph / mark beside the title (entity archetype, etc.). */
  titleExtra?: ReactNode;
  variant?: 'default' | 'dock';
}

export function PageHeader({ title, breadcrumbs, end, titleExtra, variant = 'default' }: PageHeaderProps) {
  const narrow = useMediaQuery('(max-width: 47.99em)');
  const useDockLayout = variant === 'dock' && narrow;
  const crumbItems = (breadcrumbs ?? []).map((c) => ({ label: c.label, href: c.href }));

  const titleRow = (
    <div className={styles.titleRow}>
      <Title order={1} fz={PAGE_TITLE_FZ} fw={500} className={styles.title} style={{ color: PAGE_TITLE_COLOR }}>
        {title}
      </Title>
      {titleExtra ? <div className={styles.titleExtra}>{titleExtra}</div> : null}
    </div>
  );

  if (useDockLayout) {
    return (
      <header className={styles.rootDock}>
        <div className={styles.lead}>
          {crumbItems.length ? <Breadcrumbs items={crumbItems} layout="singleLine" /> : null}
          {titleRow}
        </div>
        {end ? <div className={styles.endDock}>{end}</div> : null}
      </header>
    );
  }

  return (
    <header className={styles.root}>
      <div className={styles.lead}>
        {crumbItems.length ? <Breadcrumbs items={crumbItems} /> : null}
        {titleRow}
      </div>
      {end ? <div className={styles.end}>{end}</div> : null}
    </header>
  );
}
